/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import classnames from 'classnames';
import _ from 'lodash';
import {
  NormalButton,
  IcArrowLeft as icArrowLeft,
  IcArrowRight as icArrowRight,
} from '@/shared/ui';
import { TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { isValidHr } from '@/entities/heart-rate';
import useEventHrInfo from './useEventHrInfo';

export type EventHrInfoProps = {
  eventIndexes: any[];
  events: any[];
  isActive?: boolean;
  onClickSetEcgDisclosure?: (index: any) => void;
  aiChannel?: any;
  setSelectedEventIndex?: (index: any) => void;
  timezoneOffset?: any;
};

/**
 * Renders the detail card for an event selected on the ECG disclosure chart of the strips management tab. Reads the active event and arrow handlers from useEventHrInfo, colours the card by event type, hides the heart-rate figures for artifact and pause events, and adds a Set ECG disclosure button reporting the selected event index.
 *
 * @param props - The grouped events and indexes, active flag, timezone offset and disclosure callback.
 * @returns The event detail card with its info items and Set ECG disclosure button.
 */
const EventHrInfo = ({
  eventIndexes,
  events,
  isActive,
  onClickSetEcgDisclosure,
  aiChannel,
  setSelectedEventIndex,
  timezoneOffset,
}: EventHrInfoProps) => {
  const { eventIndex, event, channel, onClickLeft, onClickRight } = useEventHrInfo({
    eventIndexes,
    events,
    isActive,
    aiChannel,
    setSelectedEventIndex,
  });

  return (
    <div
      className={classnames(
        'event-info',
        event?.type
          ? event?.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
            ? '--artifact-event-clicked'
            : event?.type === TYPE_RHYTHM_EVENT_ENUM.AFIB
              ? '--afib-event-clicked'
              : '--event-clicked'
          : '',
        isActive ? '--active' : '',
      )}
    >
      <div className="event-info-content">
        <div className="left-container">
          {events?.length > 1 && (
            <div className="circle-navigation">
              <img onClick={onClickLeft} src={icArrowLeft} alt="icon arrow" />
              <div className="text-event-index">{`#${eventIndex + 1}`}</div>
              <img onClick={onClickRight} src={icArrowRight} alt="icon arrow" />
            </div>
          )}
          <div className="event-info-item">
            {event?.type ? (
              <div className="info-item">
                <div className="title">Start time</div>
                <div className="time">
                  {`${convertToMomentJs(event.start, timezoneOffset).format('MM/DD/YYYY, HH:mm:ss')}`}
                </div>
              </div>
            ) : (
              <div className="info-item">
                <div className="title">Time</div>
                <div className="time">
                  {`${convertToMomentJs(event.startTimeEpoch, timezoneOffset).format('MM/DD/YYYY, HH:mm:ss')}`}
                </div>
              </div>
            )}
            {channel && (
              <div className="info-item">
                <div className="title">Channel</div>
                <div className="time">{`CH${channel}`}</div>
              </div>
            )}
            {_.includes(
              [TYPE_RHYTHM_EVENT_ENUM.ARTIFACT, TYPE_RHYTHM_EVENT_ENUM.PAUSE],
              event?.type,
            ) ? null : (
              <div className="info-item --multi">
                {event?.type === TYPE_RHYTHM_EVENT_ENUM.AFIB ? (
                  <div>
                    <div className="title">Avg HR</div>
                    <strong>{isValidHr(event.avgHr) ? `${event.avgHr} bpm` : 'NA'}</strong>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="title">Min HR</div>
                      <strong>{isValidHr(event.minHr) ? `${event.minHr} bpm` : 'NA'}</strong>
                    </div>
                    <div>
                      <div className="title">Avg HR</div>
                      <strong>{isValidHr(event.avgHr) ? `${event.avgHr} bpm` : 'NA'}</strong>
                    </div>
                    <div>
                      <div className="title">Max HR</div>
                      <strong>{isValidHr(event.maxHr) ? `${event.maxHr} bpm` : 'NA'}</strong>
                    </div>
                  </>
                )}
              </div>
            )}
            {event?.type && (
              <div className="info-item">
                <div className="title">Duration</div>
                <strong>{event.duration}</strong>
              </div>
            )}
          </div>
        </div>
        <div className="right-container">
          <NormalButton
            id="set-ecg-disclosure-btn"
            className="set-ecg-disclosure-btn"
            buttonName="Set ECG disclosure"
            onClick={() => {
              onClickSetEcgDisclosure(eventIndexes[eventIndex]);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default EventHrInfo;
